import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { GameHistoryStatus, useGameHistory } from "../api/gameHistory";
import { useGameSnapshot } from "../api/gameSnapshot";
import { GameStreamEvent, useGameStream } from "../api/gameStream";
import { EndGameDialog } from "../components/EndGameDialog";
import { GameSidebar } from "../components/GameSidebar";
import { LineupPanel } from "../components/Lineup";
import { PitchTagging } from "../components/PitchTagging";
import { PlateAppearanceEditor } from "../components/PlateAppearanceEditor";
import { useActiveGame } from "../hooks/useActiveGame";
import {
  toLoadingRosters,
  useIsLoadingGameRosters,
} from "../hooks/useGameRosters";
import { useScorekeepingActions } from "../hooks/useScorekeepingActions";
import { useScorekeepingGame } from "../hooks/useScorekeepingGame";
import {
  TaggingThemeProvider,
  useTaggingTheme,
} from "../hooks/useTaggingTheme";
import { GameFullResponse } from "../types/gameFull";
import {
  getActivitiesForPA,
  getWaitingPitch,
  WAITING_PITCH_ID,
} from "../utils/plateAppearanceFeed";
import { hasFinalFills } from "../utils/tagQueue";

type GameSnapshot = ReturnType<typeof useGameSnapshot>;

type GameContentProps = {
  gameId: string;
  /** The game's first snapshot, the game state starts from it */
  initialData: GameFullResponse;
  /** Kept current by the WebSocket; a reconnect re-reads it */
  snapshot: GameSnapshot;
};

function GameContent({ gameId, initialData, snapshot }: GameContentProps) {
  const { theme } = useTaggingTheme();
  // The loader passes each re-read; only the first seeds the game state
  const [firstSnapshot] = useState(initialData);
  const game = useScorekeepingGame(
    firstSnapshot,
    toLoadingRosters(firstSnapshot.game.teams),
  );
  const {
    data,
    selectedId,
    selectedPlateAppearanceId,
    select,
    selectPlateAppearance,
    rosters,
  } = game;
  const [isLineupsOpen, setIsLineupsOpen] = useState(false);
  const { teamDisplays, machineUuid } = useActiveGame(data.game.teams);

  // Older pitches than the snapshot's load in the background
  const history = useGameHistory(gameId, snapshot.data?.next);
  // A Load More retry keeps the error until it succeeds: show it loading
  const historyStatus: GameHistoryStatus =
    history.isError && !history.isFetching
      ? "error"
      : game.isHistoryLoaded
        ? "done"
        : "loading";
  const actions = useScorekeepingActions(game, historyStatus);
  const [isEndGameOpen, setIsEndGameOpen] = useState(false);

  const { hydrate, mergeHistory, isHistoryLoaded, receiveStreamEvent } = game;
  const { receiveTagStreamEvent } = actions;
  // A re-read snapshot (reconnect) replaces the game state
  useEffect(() => {
    if (snapshot.data && snapshot.data.full !== firstSnapshot) {
      hydrate(snapshot.data.full);
    }
  }, [hydrate, snapshot.data, firstSnapshot]);
  useEffect(() => {
    if (history.data && !isHistoryLoaded) mergeHistory(history.data);
  }, [history.data, isHistoryLoaded, mergeHistory]);
  const onStreamEvent = useCallback(
    (event: GameStreamEvent) => {
      receiveStreamEvent(event);
      receiveTagStreamEvent(event);
    },
    [receiveStreamEvent, receiveTagStreamEvent],
  );
  // A reconnect may have missed deltas, so it re-reads the snapshot
  useGameStream(gameId, onStreamEvent, snapshot.refetch);

  const waitingPitch = getWaitingPitch(data);
  const isWaitingSelected = selectedId === WAITING_PITCH_ID && waitingPitch;
  const selectedActivity = data.activities.find(
    (activity) => activity.activityId === selectedId,
  );

  // Keyed by PA + pitch number so a waiting row keeps its pre-selected tags
  // when the pitch arrives and fills that slot
  const slotKey = isWaitingSelected
    ? `${waitingPitch.plateAppearanceId}#${waitingPitch.pitchNumber}`
    : selectedActivity &&
      `${selectedActivity.plateAppearanceId}#${
        getActivitiesForPA(data, selectedActivity.plateAppearanceId).findIndex(
          (activity) => activity.activityId === selectedActivity.activityId,
        ) + 1
      }`;

  return (
    <div className={theme === "dark" ? "dark" : ""} style={{ height: "100%" }}>
      {/* Relative so the lineup panel overlays just the game area */}
      <div className="relative flex h-full w-full">
        {/* Sidebar */}
        <GameSidebar
          data={data}
          teamDisplays={teamDisplays}
          selectedActivityId={selectedId}
          selectedPlateAppearanceId={selectedPlateAppearanceId}
          pitchStatuses={actions.pitchStatuses}
          historyStatus={historyStatus}
          onLoadMoreHistory={() => void history.refetch()}
          onSelectPitch={select}
          onSelectPlateAppearance={selectPlateAppearance}
          onGameStateChange={actions.editGameState}
          onChangeHomeTeam={actions.changeHomeTeam}
          onManageLineups={() => setIsLineupsOpen(true)}
          machineUuid={machineUuid}
          onChangeEndTime={actions.changeEndTime}
          onEndGame={() => setIsEndGameOpen(true)}
        />

        {/* Main Content */}
        <div className="flex min-w-0 flex-1 bg-tagging-panel">
          {selectedPlateAppearanceId ? (
            <PlateAppearanceEditor
              key={selectedPlateAppearanceId}
              data={data}
              plateAppearanceId={selectedPlateAppearanceId}
              onUpdate={actions.updatePlateAppearance}
              rosters={rosters}
              teamDisplays={teamDisplays}
              actions={actions}
              onManageLineups={() => setIsLineupsOpen(true)}
            />
          ) : isWaitingSelected || selectedActivity ? (
            <PitchTagging
              key={slotKey}
              data={data}
              activity={isWaitingSelected ? null : (selectedActivity ?? null)}
              teamDisplays={teamDisplays}
              onSave={actions.saveTags}
              isSaveDisabled={
                actions.isWaitingOnTag ||
                // A queued tag will be sent for it: locked until it is
                Boolean(selectedId && actions.pitchStatuses.has(selectedId))
              }
              onEndGame={() => setIsEndGameOpen(true)}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-tagging-muted">
              Select a pitch to tag
            </div>
          )}
        </div>

        <LineupPanel
          isOpen={isLineupsOpen}
          data={data}
          rosters={rosters}
          teamDisplays={teamDisplays}
          onSave={async (draft) => {
            // A failed save keeps the panel open with the edits
            if (await actions.saveLineups(draft)) setIsLineupsOpen(false);
          }}
          onClose={() => setIsLineupsOpen(false)}
        />
      </div>

      <EndGameDialog
        isOpen={isEndGameOpen}
        onClose={() => setIsEndGameOpen(false)}
        onConfirm={actions.endGame}
        // Only checked while it's open
        isFillingLastPitches={
          isEndGameOpen &&
          hasFinalFills(game.data, game.data.game.currentPlateAppearanceId)
        }
      />
    </div>
  );
}

/**
 * Waits for the active game's snapshot and its teams' rosters, then shows the
 * game. Keyed by game id, so a new game never starts from another game's
 * state.
 */
function GameLoader() {
  const { machineUuid, gameId, teamIds, isLoading } = useActiveGame();
  const snapshot = useGameSnapshot(gameId);
  const isLoadingRosters = useIsLoadingGameRosters(teamIds);
  const snapshotData = snapshot.data?.full;
  const shown =
    gameId && snapshotData?.game.gameId === gameId ? snapshotData : undefined;

  // No game on the SmartPark: set one up
  if (!isLoading && !gameId) {
    return <Navigate to={`/tagging-v2/${machineUuid}/setup`} replace />;
  }

  if (!gameId || !shown || isLoadingRosters) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-tagging-panel text-tagging-muted">
        {snapshot.isError ? "Couldn't load this game" : "Loading game…"}
      </div>
    );
  }

  return (
    <GameContent
      key={gameId}
      gameId={gameId}
      initialData={shown}
      snapshot={snapshot}
    />
  );
}

export function Game() {
  return (
    <TaggingThemeProvider>
      <GameLoader />
    </TaggingThemeProvider>
  );
}

export default Game;
